import polylabel from 'polylabel';

import { Coordinate, DeliveryArea, GeoRange } from 'apis/delivery-settings-api/types';

export const mapStyles = {
  light: 'mapbox://styles/woltmap/ckhd5divh05ar19mri51kjnfc',
  lightHighContrast: 'mapbox://styles/woltmap/ckhd5divh05ar19mri51kjnfc',
  dark: 'mapbox://styles/woltmap/ckf6hlfy83l951aqwhumoel6r',
};

const padding = 16;
export const fitBoundsOptions = {
  padding: { top: padding, bottom: padding, left: padding, right: padding },
};

type Bound = { lat: number; lon: number };
export type Bounds = [Bound, Bound];
export const getBounds = (deliveryAreas: Pick<DeliveryArea, 'geoRange'>[]) => {
  const coordinates = deliveryAreas.flatMap(({ geoRange }) => geoRange?.coordinates.flatMap(coords => coords));

  if (!coordinates.some(Boolean)) {
    return;
  }

  const bounds = coordinates.reduce<Bounds>(
    ([minBound, maxBound], coords) => {
      if (!coords) {
        return [minBound, maxBound];
      }
      return [
        { lat: Math.min(minBound.lat, coords.latitude), lon: Math.min(minBound.lon, coords.longitude) },
        { lat: Math.max(maxBound.lat, coords.latitude), lon: Math.max(maxBound.lon, coords.longitude) },
      ];
    },
    [
      { lat: Infinity, lon: Infinity },
      { lat: -Infinity, lon: -Infinity },
    ],
  );

  return bounds;
};

// Find the point inside that is furthest away from the outer edge (pole of inaccesibility)
export const getLabelPosition = (geoRange: GeoRange): Coordinate | undefined => {
  // If there aren't enough coordinates to form a proper polygon, return undefined
  if (geoRange.coordinates[0]?.length < 4) {
    return undefined;
  }

  // Scale coordinates to better match the 2D representation of the map in the given latitude
  // to account for earth's curvatude and not prefer longitude over latitude
  // e.g. 0.1 difference in latitude is ~11km, but the same 0.1 difference in longitude could be 11km or 5km
  const maxAbsLat = geoRange.coordinates[0].reduce((result, { latitude }) => Math.max(Math.abs(latitude), result), 0);
  const scalingFactor = Math.cos(maxAbsLat * (Math.PI / 180));
  const coordinates = geoRange.coordinates.map(coords =>
    coords.map(({ latitude, longitude }) => [longitude * scalingFactor, latitude]),
  );

  const pos = polylabel(
    coordinates,
    0.001, // ~100m precision
  );

  return { latitude: pos[1], longitude: pos[0] / scalingFactor };
};
